<template>
    <div>
        <b-row class="mb-2">
            <b-col>
                <label>
                    {{ label }}
                    <slot name="help-text"></slot>
                </label>
                <a href="#"
                    v-if="showAddMeLink && !currentWatchers?.find(w => w.id?.toString().toLowerCase() == $user.userName.toLowerCase())"
                    @click.prevent="addMeAsWatcher" class="pull-right mt-1">
                    <i class="fa fa-eye" />
                    Add Me as Watcher
                </a>
                <b-form-group>
                    <multiselect v-model="localWatchers" :options="watchersOptions" :multiple="true"
                        group-values="options" group-label="type" label="name" track-by="id"
                        :disabled="disabled" @input="emitWatchersChange">

                        <template #option="data">
                            <i v-if="data.option.isTeam" class="fa fa-users"></i>
                            <i v-else class="fa fa-user"></i>
                            {{ data.option.$groupLabel }}
                            {{ data.option.name }}
                        </template>

                        <template #tag="data">
                            <b-badge v-if="data.option.isTeam" variant="primary" class="mr-1 my-0">
                                <i class="fa fa-users"></i>
                                {{ data.option.name }}
                                <i class="fa fa-close ml-1" @click="data.remove(data.option)"></i>
                            </b-badge>
                            <b-badge v-else variant="info" class="mr-1 my-0">
                                <i class="fa fa-user"></i>
                                {{ data.option.name }}
                                <i class="fa fa-close ml-1" @click="data.remove(data.option)"></i>
                            </b-badge>
                        </template>
                    </multiselect>
                </b-form-group>
            </b-col>
        </b-row>
    </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import Multiselect from 'vue-multiselect';
import { Team } from '@/models/Team';
import { User } from '@/models/User';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

interface WatcherOption {
    id: string | number;
    name: string;
    isTeam?: boolean;
}

interface WatcherData {
    teamId?: number;
    userName?: string;
    teamName?: string;
}

@Component({
    name: "CasesWatcherComponent",
    components: {
        Multiselect
    }
})
export default class CasesWatcherComponent extends Vue {
    @Prop({ default: () => [] }) value!: WatcherData[];
    @Prop({ type: Number }) caseId?: number;
    @Prop({ type: Boolean, default: false }) disabled!: boolean;
    @Prop({ type: Boolean, default: true }) showAddMeLink!: boolean;
    @Prop({ type: String, default: 'Watchers' }) label!: string;

    protected teams: Team[] = [];
    protected users: User[] = [];
    private localWatchers: WatcherOption[] = [];
    private isInitializing = true;

    async mounted() {
        try {
            await this.loadTeamsAndUsers();
            await this.initializeWatchers();
            this.isInitializing = false;
        } catch (error) {
            console.error('Error in CasesWatcherComponent mounted:', error);
            this.isInitializing = false;
        }
    }

    async loadTeamsAndUsers() {
        try {
            await Promise.all([
                this.getTeams(),
                this.getUsers()
            ]);
        } catch (error) {
            console.error('Error loading teams and users:', error);
        }
    }

    async getTeams() {
        try {
            const response = await this.$http.get<Team[]>("/user/teams");
            this.teams = response.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    async getUsers() {
        try {
            const response = await this.$http.get<User[]>("/user");
            this.users = response.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    async initializeWatchers() {
        if (this.caseId && (!this.value || this.value.length === 0)) {
            try {
                const response = await this.$http.get<WatcherData[]>(`/api/Case/${this.caseId}/watchers`);
                this.setLocalWatchers(response.data);
                this.emitWatchersChange();
                return;
            } catch (err) {
                console.error('Error loading watchers from API:', err);
            }
        }
        this.setLocalWatchers(this.value);
    }

    private setLocalWatchers(watchersData: WatcherData[]) {
        this.localWatchers = watchersData?.map(w => ({
            id: w.teamId ? w.teamId : w.userName!,
            name: w.teamId ? w.teamName! : w.userName!,
            isTeam: !!w.teamId
        })) ?? [];
    }

    get watchersOptions() {
        return [
            {
                type: 'Teams',
                options: this.teams.map(t => ({
                    id: t.id,
                    name: t.name,
                    isTeam: true
                }))
            },
            {
                type: 'Users',
                options: this.users.map(u => ({
                    id: u.userName,
                    name: u.userName,
                    isTeam: false
                }))
            }
        ]
    }

    get currentWatchers() {
        return this.localWatchers;
    }

    @Watch('value', { deep: true })
    onValueChanged() {
        if (this.isInitializing || this.caseId) {
            return;
        }
        this.setLocalWatchers(this.value);
    }

    @Watch('caseId')
    async onCaseIdChanged() {
        if (this.isInitializing) {
            return;
        }
        this.localWatchers = [];
        await this.initializeWatchers();
    }

    private emitWatchersChange() {
        const watchers = this.localWatchers.map(w => ({
            teamId: w.isTeam ? w.id as number : undefined,
            userName: !w.isTeam ? w.id as string : undefined,
            teamName: w.isTeam ? w.name : undefined
        }));

        this.$emit('input', watchers);
    }

    addMeAsWatcher() {
        const userName = this.$user?.userName;
        if (!userName || this.localWatchers.find(w => w.id?.toString().toLowerCase() === userName.toLowerCase())) return;

        this.localWatchers.push({
            id: userName,
            name: userName,
            isTeam: false
        });

        // Manually emit after user action
        this.emitWatchersChange();
    }
}
</script>

<style scoped>
@import "vue-multiselect/dist/vue-multiselect.min.css";
</style>
